import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  Grid,
  Box,
  Typography,
  SelectChangeEvent,
  useTheme,
} from "@mui/material";
import { BRAND_ITEM_FIXED_VALUE } from "@/const";
import { useCartContext } from "@/context/CartContext";
import { useProductCatalogContext } from "@/context/ProductCatalogContext";
import { useNavigationHistory } from "@/context/NavigationHistoryContext";
import { BackLink } from "@/components/backLink";
import { ProductImageGallery } from "./ProductImageGallery";
import { ProductTitle } from "./ProductTitle";
import { ProductPurchaseCard } from "./ProductPurchaseCard";
import { ProductFeatures } from "./ProductFeatures";
import { ProductSpecifications } from "./ProductSpecifications";
import { ImageModal } from "./ImageModal";

export const ProductInfo = () => {
  const { selectedProduct, loading } = useProductCatalogContext();
  const [submitting, setSubmitting] = useState(false);
  const [selectedImage, setSelectedImage] = useState(0);
  const [quantity, setQuantity] = useState(1);
  const [imageModalOpen, setImageModalOpen] = useState(false);
  const [modalImageIndex, setModalImageIndex] = useState(0);
  const { addZincItem } = useCartContext();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { previousLocation } = useNavigationHistory();
  const theme = useTheme();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const handleSubmit = async () => {
    if (!selectedProduct || loading) {
      throw new Error("No product selected");
    }
    setSubmitting(true);
    addZincItem({
      ...selectedProduct,
      utid: selectedProduct.product_id,
      amount: selectedProduct.amount,
      type: BRAND_ITEM_FIXED_VALUE,
      quantity,
    });

    navigate("/shop/products");
  };

  const handleQuantityChange = (event: SelectChangeEvent<number>) => {
    setQuantity(Number(event.target.value));
  };

  const isSubmitDisabled = () => !selectedProduct?.product_id || loading;

  const handleSetProductVariantId = (event: SelectChangeEvent<string>) => {
    navigate(
      `/shop/products/${event.target.value}?${searchParams.toString()}`,
      { replace: true },
    );
  };

  const handleImageClick = () => {
    setModalImageIndex(selectedImage);
    setImageModalOpen(true);
  };

  const handleModalClose = () => {
    setImageModalOpen(false);
  };

  const handleModalImageChange = (direction: "prev" | "next") => {
    const images = selectedProduct?.images || [];
    if (images.length === 0) return;

    if (direction === "prev") {
      setModalImageIndex((prev) => (prev === 0 ? images.length - 1 : prev - 1));
    } else {
      setModalImageIndex((prev) => (prev === images.length - 1 ? 0 : prev + 1));
    }
  };

  const reviewCount =
    selectedProduct?.num_reviews || selectedProduct?.review_count || 0;

  // Check if we came from a shop/products path
  const cameFromProductSearch =
    previousLocation?.pathname.startsWith("/shop/products") ?? false;

  return (
    <Box data-cypress="product-info-page" sx={{ pb: { xs: 3, md: 4 } }}>
      <Box sx={{ marginBottom: { xs: 2, md: 3 } }}>
        {cameFromProductSearch ? (
          <BackLink to="" onClick={() => navigate(-1)}>
            Back to Search Results
          </BackLink>
        ) : (
          <BackLink to="/shop/products">Back to Products Search</BackLink>
        )}
      </Box>
      <Grid container spacing={{ xs: 2, sm: 3, md: 4 }}>
        {/* Mobile Title and Rating */}
        <Grid size={{ xs: 12 }} display={{ xs: "block", md: "none" }}>
          <ProductTitle
            title={selectedProduct?.title}
            stars={selectedProduct?.stars}
            reviewCount={reviewCount}
            isLoading={loading && !selectedProduct?.title}
            variant="mobile"
          />
        </Grid>

        {/* Product Image Column */}
        <Grid size={{ xs: 12, md: 5 }}>
          <ProductImageGallery
            images={selectedProduct?.images}
            mainImage={selectedProduct?.image || selectedProduct?.main_image}
            title={selectedProduct?.title}
            selectedImage={selectedImage}
            onImageSelect={setSelectedImage}
            onImageClick={handleImageClick}
            isLoading={
              loading &&
              !selectedProduct?.images &&
              !selectedProduct?.image &&
              !selectedProduct?.main_image
            }
          />

          {/* Mobile Compact Product Card - Below Image */}
          <Grid size={{ xs: 12 }} display={{ xs: "block", md: "none" }}>
            <ProductPurchaseCard
              variants={selectedProduct?.all_variants}
              selectedVariantId={selectedProduct?.product_id}
              onVariantChange={handleSetProductVariantId}
              quantity={quantity}
              onQuantityChange={handleQuantityChange}
              amount={selectedProduct?.amount || 0}
              onAddToCart={handleSubmit}
              disabled={isSubmitDisabled()}
              submitting={submitting}
              isLoading={loading}
              variant="mobile"
            />
          </Grid>
        </Grid>

        {/* Product Details Column */}
        <Grid size={{ xs: 12, md: 7 }} display={{ xs: "none", md: "block" }}>
          {/* Desktop Title and Rating */}
          <Grid size={{ xs: 12 }}>
            <ProductTitle
              title={selectedProduct?.title}
              stars={selectedProduct?.stars}
              reviewCount={reviewCount}
              isLoading={loading && !selectedProduct?.title}
              variant="desktop"
            />
          </Grid>

          {/* Desktop Compact Product Card - Above the Fold */}
          <Grid size={{ xs: 12 }} display={{ xs: "none", md: "block" }}>
            <ProductPurchaseCard
              variants={selectedProduct?.all_variants}
              selectedVariantId={selectedProduct?.product_id}
              onVariantChange={handleSetProductVariantId}
              quantity={quantity}
              onQuantityChange={handleQuantityChange}
              amount={selectedProduct?.amount || 0}
              onAddToCart={handleSubmit}
              disabled={isSubmitDisabled()}
              submitting={submitting}
              isLoading={loading}
              variant="desktop"
            />
          </Grid>
        </Grid>

        {/* Mobile Section Header */}
        <Grid size={{ xs: 12 }} display={{ xs: "block", md: "none" }}>
          <Box
            sx={{
              padding: { xs: 2, sm: 2.5 },
            }}
          >
            <Typography
              sx={{
                fontSize: { xs: 30, sm: 36 },
                fontWeight: 700,
                textAlign: "center",
                letterSpacing: 1,
                background: `linear-gradient(135deg, ${theme.palette.secondary.main} 0%, ${theme.palette.secondary.dark || theme.palette.secondary.main} 100%)`,
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
                backgroundClip: "text",
              }}
            >
              Product Details
            </Typography>
          </Box>
        </Grid>

        <ProductFeatures
          features={selectedProduct?.feature_bullets}
          isLoading={loading && !selectedProduct?.feature_bullets}
        />
      </Grid>

      {/* Specifications Card - Full Width */}
      <ProductSpecifications
        details={selectedProduct?.product_details}
        isLoading={loading && !selectedProduct?.product_details}
      />

      {/* Image Modal */}
      <ImageModal
        open={imageModalOpen}
        onClose={handleModalClose}
        images={selectedProduct?.images}
        currentIndex={modalImageIndex}
        onIndexChange={handleModalImageChange}
        productTitle={selectedProduct?.title}
        mainImage={selectedProduct?.image || selectedProduct?.main_image}
      />
    </Box>
  );
};
